/**
 * The Fan panel's blocks, one component each, so the Workspace Fan panel and
 * the Chats page's side column (ADR 0024) draw the same thing. Each block is a
 * collapsible section; which ones are open is a per-viewer convenience kept in
 * browser storage.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { Spinner } from '../components/Spinner';
import { useMediaUrl } from './media';
import { aiStatusLabel, money } from './format';
import type { SectionsOpen } from './sectionsOpen';
import type { FanData } from './useFan';

export function BuyingPowerDots({ value, size = 7 }: { value: number | null; size?: number }) {
  const { t } = useTranslation('chat');
  // Unknown: five empty circles, not a label.
  const label = value == null ? t('fan.bpUnknown') : `${t('fan.buyingPower')} ${value}/5`;
  return (
    <span className={`rc-bp ${value == null ? 'rc-bp--unknown' : ''}`} role="img" title={label} aria-label={label}>
      {[0, 1, 2, 3, 4].map((k) => (
        <span key={k} style={{ width: size, height: size }} className={value != null && k < value ? 'is-on' : ''} />
      ))}
    </span>
  );
}

export function Section({
  id,
  icon,
  label,
  summary,
  tools,
  sections,
  children,
}: {
  id: string;
  icon: string;
  label: string;
  summary?: ReactNode;
  /** Extra header buttons (refresh). */
  tools?: ReactNode;
  sections: SectionsOpen;
  children: ReactNode;
}) {
  const open = sections.isOpen(id);
  return (
    <section className="rc-fan-sec" aria-label={label}>
      <div className="rc-fan-sec-row">
        <button type="button" className="rc-fan-sec-head" onClick={() => sections.toggle(id)} aria-expanded={open}>
          <FeatherIcon name={icon} size={14} />
          <span className="rc-fan-sec-label">{label}</span>
          {summary != null && summary !== '' && <span className="rc-fan-sec-sum">{summary}</span>}
          <FeatherIcon name="chevron-right" size={14} className={`rc-fan-sec-chev ${open ? 'is-open' : ''}`} />
        </button>
        {tools}
      </div>
      {open && <div className="rc-fan-sec-body">{children}</div>}
    </section>
  );
}

/** Appended text for a note field; `n` changes on every append so the same text can be added twice. */
export interface NoteAppend {
  text: string;
  n: number;
}

function NoteField({ value, placeholder, label, onSave, append }: { value: string; placeholder: string; label: string; onSave: (v: string) => Promise<void>; append?: NoteAppend | null }) {
  const { t } = useTranslation('chat');
  const [draft, setDraft] = useState(value);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => setDraft(value), [value]);
  // An AI suggestion goes on its own line, unsaved, with the caret after it (chats.js appendChatFanNoteTemplate).
  useEffect(() => {
    if (!append) return;
    setDraft((d) => `${d}${d && !d.endsWith('\n') ? '\n' : ''}${append.text}`.slice(0, 5000));
    setState('idle');
    const el = box.current;
    if (el) {
      el.focus();
      window.setTimeout(() => (el.selectionStart = el.selectionEnd = el.value.length), 0);
    }
  }, [append]);
  return (
    <div className="rc-note">
      <textarea
        ref={box}
        rows={2}
        value={draft}
        aria-label={label}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          setState('idle');
        }}
        onBlur={async () => {
          if (draft === value) return;
          setState('saving');
          try {
            await onSave(draft);
            setState('saved');
          } catch {
            setState('error');
          }
        }}
      />
      {state !== 'idle' && <span className={`rc-note-state ${state === 'error' ? 'rc-error' : ''}`}>{t(`fan.note.${state}`)}</span>}
    </div>
  );
}

function sinceLabel(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: '2-digit' }) : '—';
}

function lastSeenLabel(fan: FanData['profile'], t: (k: string, o?: Record<string, unknown>) => string, locale: string): string {
  if (!fan) return '—';
  if (fan.isOnline) return t('fan.now');
  if (!fan.lastSeen) return '—';
  const d = new Date(fan.lastSeen);
  if (!Number.isFinite(d.getTime())) return fan.lastSeen;
  const day = d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}, ${time}`;
}

/** Who the fan is: avatar, name, @handle, since / subscribed / last seen. */
export function FanIdentity({ accountId, fanId, fan, name, tools }: { accountId: string; fanId: string; fan: FanData; name?: string | null; tools?: ReactNode }) {
  const { t, i18n } = useTranslation('chat');
  const p = fan.profile;
  const avatar = useMediaUrl(accountId, p?.avatar);
  const shown = name || p?.name;
  return (
    <div className="rc-fan-id">
      <div className="rc-fan-who">
        <span className="rc-avatar rc-avatar--40">{avatar.url ? <img src={avatar.url} alt="" /> : (shown || '?').slice(0, 1).toUpperCase()}</span>
        <div className="rc-fan-names">
          <span className="rc-fan-name">{shown || (fan.loading ? <Spinner size={14} /> : fanId)}</span>
          {p?.username && <span className="rc-muted">@{p.username}</span>}
        </div>
        <button type="button" className="rc-icon-btn" title={t('fan.refresh')} aria-label={t('fan.refresh')} onClick={fan.reload}>
          <FeatherIcon name="refresh-cw" size={14} />
        </button>
        {tools}
      </div>
      {fan.profileError && <span className="rc-error">{fan.profileError}</span>}
      <div className="rc-fan-tiles">
        {[
          [t('fan.since'), sinceLabel(p?.subscribedAt ?? null, i18n.language)],
          [t('fan.subscribed'), p?.duration || '—'],
          [t('fan.lastSeen'), lastSeenLabel(p, t, i18n.language)],
        ].map(([label, value]) => (
          <div key={label} title={`${label}: ${value}`}>
            <span>{label}</span>
            <b>{value}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * How many PPVs the fan bought, or null when it can't be said: the count comes
 * from synced transactions, the PPV amount from OnlyFans itself, so a count of
 * 0 next to a paid amount (purchases the sync hasn't seen) is not shown.
 */
function ppvBought(fan: Pick<FanData, 'profile' | 'ppvStats'>): number | null {
  const stats = fan.ppvStats;
  if (!stats) return null;
  // 0 next to a paid (or unknown) PPV amount says nothing true.
  if (stats.bought === 0 && fan.profile?.spent.ppv !== 0) return null;
  return stats.bought;
}

export function SpendingBlock({ fan, sections }: { fan: FanData; sections: SectionsOpen }) {
  const { t } = useTranslation('chat');
  const p = fan.profile;
  const bought = ppvBought(fan);
  const avgPpv = bought != null && bought > 0 && p?.spent.ppv != null ? p.spent.ppv / bought : null;
  return (
    <Section id="spend" icon="dollar-sign" label={t('fan.spending')} summary={money(p?.spent.total)} sections={sections}>
      <div className="rc-fan-grid">
        <span>{t('fan.spent')}</span>
        <b>{money(p?.spent.total)}</b>
        <span>{t('fan.tips')}</span>
        <span>{money(p?.spent.tips)}</span>
        <span>{t('fan.ppv')}</span>
        <span>
          {money(p?.spent.ppv)}
          {bought != null ? ` · ${t('fan.bought', { count: bought })}` : ''}
        </span>
        <span>{t('fan.avgPpv')}</span>
        <span>{avgPpv == null ? '—' : money(Math.round(avgPpv * 100) / 100)}</span>
        <span>{t('fan.buyingPower')}</span>
        <span className="rc-fan-grid-end">
          <BuyingPowerDots value={fan.buyingPower} />
        </span>
      </div>
    </Section>
  );
}

export function ListsBlock({ fan, sections }: { fan: FanData; sections: SectionsOpen }) {
  const { t } = useTranslation('chat');
  const [picker, setPicker] = useState(false);
  const [listQuery, setListQuery] = useState('');
  const [listBusy, setListBusy] = useState<string | null>(null);
  const memberLists = fan.lists.filter((l) => fan.memberOf.has(l.id));
  const shownLists = fan.lists.filter((l) => l.name.toLowerCase().includes(listQuery.trim().toLowerCase()));
  return (
    <Section id="lists" icon="list" label={t('fan.lists')} summary={String(memberLists.length)} sections={sections}>
      <div className="rc-fan-lists">
        {memberLists.map((l) => (
          <span key={l.id} className="rc-list-chip">
            {l.name}
          </span>
        ))}
        <button type="button" className="rc-list-chip rc-list-chip--add" title={t('fan.editLists')} onClick={() => setPicker((v) => !v)}>
          <FeatherIcon name={picker ? 'x' : 'plus'} size={12} />
        </button>
      </div>
      {picker && (
        <div className="rc-list-picker">
          <input value={listQuery} onChange={(e) => setListQuery(e.target.value)} placeholder={t('fan.findList')} />
          <div className="rc-list-options">
            {shownLists.map((l) => {
              const member = fan.memberOf.has(l.id);
              return (
                <button
                  key={l.id}
                  type="button"
                  className={`rc-list-option ${member ? 'is-on' : ''}`}
                  disabled={listBusy === l.id}
                  onClick={async () => {
                    setListBusy(l.id);
                    try {
                      await fan.setMembership(l.id, !member);
                    } catch {
                      /* the chip simply stays as it was */
                    } finally {
                      setListBusy(null);
                    }
                  }}
                >
                  <span className="rc-check">{member && <FeatherIcon name="check" size={10} />}</span>
                  {l.name}
                </button>
              );
            })}
            {!shownLists.length && <span className="rc-muted">{t('fan.noLists')}</span>}
          </div>
        </div>
      )}
    </Section>
  );
}

export interface NoteSuggestion {
  label: string;
  template: string;
  kind: string;
}

export function NotesBlock({ fan, sections, suggestions = [] }: { fan: FanData; sections: SectionsOpen; suggestions?: NoteSuggestion[] }) {
  const { t } = useTranslation('chat');
  const [append, setAppend] = useState<NoteAppend | null>(null);
  const [used, setUsed] = useState<Set<string>>(new Set());
  return (
    <Section id="notes" icon="sticky-note" label={t('fan.notes')} sections={sections}>
      <span className="rc-fan-note-label">{t('fan.teamNote')}</span>
      <NoteField value={fan.note} label={t('fan.teamNote')} placeholder={t('fan.teamNotePlaceholder')} onSave={fan.saveNote} append={append} />
      {suggestions.length > 0 && (
        <div className="rc-note-suggest">
          <span className="rc-fan-note-label">{t('fan.suggestions')}</span>
          <div className="rc-note-suggest-row">
            {suggestions.map((s) => (
              <button
                key={s.template}
                type="button"
                className={`rc-note-suggest-chip ${used.has(s.template) ? 'is-used' : ''}`}
                title={s.template}
                onClick={() => {
                  setAppend((prev) => ({ text: s.template, n: (prev?.n ?? 0) + 1 }));
                  setUsed((prev) => new Set(prev).add(s.template));
                }}
              >
                + {t(`fan.suggestKind.${s.kind}`, { defaultValue: s.kind })}: {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <span className="rc-fan-note-label">{t('fan.ofNote')}</span>
      <NoteField value={fan.profile?.ofNote ?? ''} label={t('fan.ofNote')} placeholder={t('fan.ofNotePlaceholder')} onSave={fan.saveOfNote} />
    </Section>
  );
}

export function AiStatusBlock({ aiStatus, sections }: { aiStatus?: string | null; sections: SectionsOpen }) {
  const { t } = useTranslation('chat');
  return (
    <Section id="ai" icon="sparkles" label={t('fan.ai')} summary={aiStatusLabel(aiStatus, t)} sections={sections}>
      <span className="rc-ai-status">
        <FeatherIcon name="bot" size={13} />
        {aiStatusLabel(aiStatus, t)}
      </span>
    </Section>
  );
}
